import { useRef, useState, useEffect, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Text, Billboard } from "@react-three/drei";
import {
  Group,
  Vector3,
  Mesh,
  Object3D,
  InstancedMesh,
  ShaderMaterial,
  Color,
  AdditiveBlending,
  DoubleSide,
} from "three";
import gsap from "gsap";
import { useSlashSound } from "../audio/useSlashSound";
import { checkCollision } from "./collision";
import { triggerHitFlash, HitFlashSlot } from "./hitFlash";
import {
  getCutGrade,
  BASE_POINTS,
  comboMultiplier,
  GRADE_COLORS,
  tenouchiBonus,
  DIRECTION_BONUS,
  TENOUCHI_SNAP_THRESHOLD,
} from "./grading";
import { useGameStore, Grade } from "./useGameStore";
import { Note } from "./beatmap";
import { TargetDefaults, createTargetSpec } from "./targetSystem";
import { colors as themeColors } from "../ui/theme";
import { SPAWN_Z, MISS_Z, DIRECTION_DOT_THRESHOLD } from "./constants";

// Fresnel rim — the silhouette edge glows, which is what separates a
// "designed target" from a crude flat-shaded ball. uIntensity is ramped
// per-frame as the block nears the strike window (approach telegraph).
const RIM_VERT = `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vViewDir = normalize(-mvPosition.xyz);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const RIM_FRAG = `
  uniform vec3 uColor;
  uniform vec3 uArchetypeColor;
  uniform float uPower;
  uniform float uIntensity;
  uniform float uTelegraph;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  void main() {
    float rim = pow(1.0 - clamp(dot(normalize(vNormal), normalize(vViewDir)), 0.0, 1.0), uPower);
    vec3 col = mix(uArchetypeColor, uColor, uTelegraph);
    gl_FragColor = vec4(col, rim * uIntensity);
  }
`;

// Rim color mixes the archetype's own hue with the (shared, amber) telegraph
// color instead of replacing it outright — the telegraph ramp used to push
// uColor straight to full amber for every archetype, so a cool-hued target
// (standard, #46A6C6) and a warm one (fast, already amber-adjacent) both read
// as amber/white by the time they're close enough to matter, defeating the
// "COLOR = archetype" rule the target visual-language doc calls for at every
// approach distance, not just at rest. Capping the mix below 1.0 keeps the
// archetype hue as the majority contributor even at closest approach, so the
// telegraph still reads as "getting warmer/urgent" without erasing identity.
const TELEGRAPH_MIX_MAX = 0.42;

function useRimMaterial(telegraphColor: string, archetypeColor: string) {
  const mat = useMemo(
    () =>
      new ShaderMaterial({
        uniforms: {
          uColor: { value: new Color(telegraphColor) },
          uArchetypeColor: { value: new Color(archetypeColor) },
          uPower: { value: 2.5 },
          uIntensity: { value: 0.6 },
          uTelegraph: { value: 0 },
        },
        vertexShader: RIM_VERT,
        fragmentShader: RIM_FRAG,
        transparent: true,
        depthWrite: false,
        blending: AdditiveBlending,
        side: DoubleSide,
      }),
    [telegraphColor, archetypeColor],
  );
  useEffect(() => () => mat.dispose(), [mat]);
  return mat;
}

const ENERGY_BODY_VERT = `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vViewDir = normalize(-mvPosition.xyz);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const ENERGY_BODY_FRAG = `
  uniform vec3 uBase;
  uniform vec3 uCore;
  uniform vec3 uAccent;
  uniform vec3 uShadow;
  uniform float uOpacity;
  uniform float uPulse;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  void main() {
    vec3 n = normalize(vNormal);
    vec3 v = normalize(vViewDir);
    float facing = clamp(dot(n, v), 0.0, 1.0);
    float center = pow(facing, 1.35);
    float rim = pow(1.0 - facing, 1.9);
    float highlight = pow(max(dot(n, normalize(vec3(-0.36, 0.72, 0.58))), 0.0), 10.0);
    float inner = pow(center, 2.15) * (0.72 + uPulse * 0.75);
    vec3 col = mix(uShadow, uBase, 0.42 + center * 0.48);
    col += uCore * inner * 0.78;
    col += uAccent * highlight * (1.15 + uPulse * 0.55);
    col += uBase * rim * (0.22 + uPulse * 0.12);
    float alpha = uOpacity * (0.66 + center * 0.2 + rim * 0.14);
    gl_FragColor = vec4(col, alpha);
  }
`;

function useEnergyBodyMaterial({
  base,
  core,
  accent,
  shadow,
  opacity,
}: {
  base: string;
  core: string;
  accent: string;
  shadow: string;
  opacity: number;
}) {
  const mat = useMemo(
    () =>
      new ShaderMaterial({
        uniforms: {
          uBase: { value: new Color(base) },
          uCore: { value: new Color(core) },
          uAccent: { value: new Color(accent) },
          uShadow: { value: new Color(shadow) },
          uOpacity: { value: opacity },
          uPulse: { value: 0 },
        },
        vertexShader: ENERGY_BODY_VERT,
        fragmentShader: ENERGY_BODY_FRAG,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      }),
    [base, core, accent, shadow, opacity],
  );
  useEffect(() => () => mat.dispose(), [mat]);
  return mat;
}

const ENERGY_GLOW_VERT = `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vViewDir = normalize(-mvPosition.xyz);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const ENERGY_GLOW_FRAG = `
  uniform vec3 uColor;
  uniform vec3 uAccent;
  uniform float uOpacity;
  uniform float uFalloff;
  uniform float uPulse;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  void main() {
    float facing = clamp(dot(normalize(vNormal), normalize(vViewDir)), 0.0, 1.0);
    float glow = pow(facing, uFalloff);
    vec3 col = mix(uColor, uAccent, pow(facing, 2.2)) * (0.9 + uPulse * 0.55);
    float alpha = uOpacity * glow * (0.82 + uPulse * 0.62);
    gl_FragColor = vec4(col, alpha);
  }
`;

function useEnergyGlowMaterial({
  color,
  accent,
  opacity,
  falloff,
}: {
  color: string;
  accent: string;
  opacity: number;
  falloff: number;
}) {
  const mat = useMemo(
    () =>
      new ShaderMaterial({
        uniforms: {
          uColor: { value: new Color(color) },
          uAccent: { value: new Color(accent) },
          uOpacity: { value: opacity },
          uFalloff: { value: falloff },
          uPulse: { value: 0 },
        },
        vertexShader: ENERGY_GLOW_VERT,
        fragmentShader: ENERGY_GLOW_FRAG,
        transparent: true,
        depthWrite: false,
        blending: AdditiveBlending,
        side: DoubleSide,
        toneMapped: false,
      }),
    [color, accent, opacity, falloff],
  );
  useEffect(() => () => mat.dispose(), [mat]);
  return mat;
}

const REFERENCE_CAM_Y = 1.6;
const DIAGONAL = Math.SQRT1_2;

const DIR_VECTORS: Record<string, Vector3 | null> = {
  right: new Vector3(1, 0, 0),
  left: new Vector3(-1, 0, 0),
  up: new Vector3(0, 1, 0),
  down: new Vector3(0, -1, 0),
  "up-left": new Vector3(-DIAGONAL, DIAGONAL, 0),
  "up-right": new Vector3(DIAGONAL, DIAGONAL, 0),
  "down-left": new Vector3(-DIAGONAL, -DIAGONAL, 0),
  "down-right": new Vector3(DIAGONAL, -DIAGONAL, 0),
  any: null,
};

// The direction notch is front-mounted on the target body and drawn pointing +X
// (see DirectionChevron below). Its apex points the way the blade should
// actually travel — the same convention as DIR_VECTORS, the TeachPanel glyphs,
// and the kata labels (a "down" note's notch points down, matching a
// shomen-giri's downward cut). This is the default as of 2026-09, chosen
// after player feedback that the original v1 "cut against the notch" reading
// (DIR_ARROW_ROT_AGAINST below) was unintuitive; the setting only flips this
// visual, never the required cut in DIR_VECTORS.
const DIR_ARROW_ROT: Record<string, [number, number, number]> = {
  right: [0, 0, 0],
  left: [0, 0, Math.PI],
  up: [0, 0, Math.PI / 2],
  down: [0, 0, -Math.PI / 2],
  "up-left": [0, 0, (3 * Math.PI) / 4],
  "up-right": [0, 0, Math.PI / 4],
  "down-left": [0, 0, (-3 * Math.PI) / 4],
  "down-right": [0, 0, -Math.PI / 4],
};

// The v1 orientation — notch points opposite the required cut. Kept only for
// the "Cut arrow: Against" settings option (Menu.tsx / MenuScreen3D).
const DIR_ARROW_ROT_AGAINST: Record<string, [number, number, number]> = {
  right: [0, 0, Math.PI],
  left: [0, 0, 0],
  up: [0, 0, -Math.PI / 2],
  down: [0, 0, Math.PI / 2],
  "up-left": [0, 0, -Math.PI / 4],
  "up-right": [0, 0, (-3 * Math.PI) / 4],
  "down-left": [0, 0, Math.PI / 4],
  "down-right": [0, 0, (3 * Math.PI) / 4],
};

const GRADE_KANJI: Record<string, string> = {
  IPPON: "一本",
  PERFECT: "技あり",
  GREAT: "素晴",
  GOOD: "良い",
  LATE: "可",
  MISS: "外れ",
};

const GRADE_ENGLISH: Record<string, string> = {
  IPPON: "IPPON",
  PERFECT: "EXCELLENT",
  GREAT: "GREAT",
  GOOD: "GOOD",
  LATE: "LATE",
  MISS: "MISS",
};

const PARTICLE_COUNT = 14;
const particleDummy = new Object3D();

function CutParticles({ color }: { color: string }) {
  const ref = useRef<InstancedMesh | null>(null);
  const data = useRef(
    Array.from({ length: PARTICLE_COUNT }, () => ({
      pos: new Vector3(
        (Math.random() - 0.5) * 0.1,
        (Math.random() - 0.5) * 0.1,
        (Math.random() - 0.5) * 0.1,
      ),
      vel: new Vector3(
        (Math.random() - 0.5) * 3.2,
        Math.random() * 2.8 + 0.4,
        (Math.random() - 0.5) * 3.2,
      ),
      life: 1.0,
    })),
  );

  useFrame((_, delta) => {
    if (!ref.current) return;
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const p = data.current[i];
      p.life -= delta * 2.4;
      p.vel.y -= delta * 5.0;
      p.pos.addScaledVector(p.vel, delta);
      const s = Math.max(0, p.life) * 0.065;
      particleDummy.position.copy(p.pos);
      particleDummy.scale.setScalar(s);
      particleDummy.updateMatrix();
      ref.current.setMatrixAt(i, particleDummy.matrix);
    }
    ref.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={ref} args={[undefined, undefined, PARTICLE_COUNT]}>
      <icosahedronGeometry args={[1, 0]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={1.8}
      />
    </instancedMesh>
  );
}

function DirectionChevron({
  size,
  accent,
  shadow,
}: {
  size: number;
  accent: string;
  shadow: string;
}) {
  const armLength = size * 0.42;
  const armWidth = size * 0.074;
  const gap = size * 0.072;
  const x = size * 0.035;

  return (
    <group>
      <mesh
        position={[x + 0.012, gap - 0.005, 0.008]}
        rotation={[0, 0, -Math.PI / 4]}
      >
        <boxGeometry args={[armLength, armWidth * 1.7, 0.018]} />
        <meshStandardMaterial
          color={shadow}
          emissive={shadow}
          emissiveIntensity={1.0}
          toneMapped={false}
        />
      </mesh>
      <mesh
        position={[x + 0.012, -gap - 0.005, 0.008]}
        rotation={[0, 0, Math.PI / 4]}
      >
        <boxGeometry args={[armLength, armWidth * 1.7, 0.018]} />
        <meshStandardMaterial
          color={shadow}
          emissive={shadow}
          emissiveIntensity={1.0}
          toneMapped={false}
        />
      </mesh>
      <mesh position={[x, gap, 0.024]} rotation={[0, 0, -Math.PI / 4]}>
        <boxGeometry args={[armLength, armWidth, 0.018]} />
        <meshStandardMaterial
          color={accent}
          emissive={accent}
          emissiveIntensity={4.2}
          toneMapped={false}
        />
      </mesh>
      <mesh position={[x, -gap, 0.024]} rotation={[0, 0, Math.PI / 4]}>
        <boxGeometry args={[armLength, armWidth, 0.018]} />
        <meshStandardMaterial
          color={accent}
          emissive={accent}
          emissiveIntensity={4.2}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}

interface BlockProps {
  note: Note;
  noteId: number;
  spawnCamY: number;
  scheduledSpawnTime: number;
  targetDefaults: TargetDefaults;
  getCurrentTime: () => number;
  onRemove: (wasHit: boolean, points: number) => void;
}

type SliceState = "none" | "hit" | "miss";

function requiredHandForWield(
  noteHand: Note["hand"],
  wield: ReturnType<typeof useGameStore.getState>["settings"]["wield"],
): Note["hand"] | undefined {
  if (wield !== "dual") return undefined;
  return noteHand === "any" ? undefined : noteHand;
}

export function Block({
  note,
  noteId,
  spawnCamY,
  scheduledSpawnTime,
  targetDefaults,
  getCurrentTime,
  onRemove,
}: BlockProps) {
  const worldY = spawnCamY + (note.height - REFERENCE_CAM_Y);
  const target = useMemo(
    () => createTargetSpec(note, targetDefaults),
    [note, targetDefaults],
  );
  const color = target.color;
  const shadowColor = target.shadow;

  const groupRef = useRef<Group | null>(null);
  const topRef = useRef<Mesh | null>(null);
  const bottomRef = useRef<Mesh | null>(null);
  const topMatRef = useRef<import("three").MeshStandardMaterial | null>(null);
  const bottomMatRef = useRef<import("three").MeshStandardMaterial | null>(
    null,
  );
  const flashSlotRef = useRef<HitFlashSlot | null>(null);
  const gradeGroupRef = useRef<Group | null>(null);
  const hitRef = useRef(false);
  const wasHitRef = useRef(false);
  const pointsRef = useRef(0);
  const tmpPos = useRef(new Vector3());

  const [slice, setSlice] = useState<SliceState>("none");
  const gradeColor = useRef("#ffffff");
  const gradeKanji = useRef("");
  const gradeEnglish = useRef("");
  const comboLabel = useRef("");
  const tenouchiBadge = useRef(false);
  const rimMat = useRimMaterial(target.telegraphColor, target.color);
  const bodyMat = useEnergyBodyMaterial({
    base: target.color,
    core: target.coreColor,
    accent: target.accent,
    shadow: target.shadow,
    opacity: target.bodyOpacity,
  });
  const auraMat = useEnergyGlowMaterial({
    color: target.color,
    accent: target.coreColor,
    opacity: target.auraOpacity,
    falloff: 1.35,
  });
  const coreMat = useEnergyGlowMaterial({
    color: target.coreColor,
    accent: target.accent,
    opacity: target.coreOpacity,
    falloff: 1.05,
  });
  const highlightMat = useEnergyGlowMaterial({
    color: target.accent,
    accent: "#ffffff",
    opacity: target.highlightOpacity,
    falloff: 0.95,
  });
  const trailMat = useEnergyGlowMaterial({
    color: target.color,
    accent: target.coreColor,
    opacity: target.motionTrailOpacity,
    falloff: 1.2,
  });
  const playSlashSound = useSlashSound();

  const {
    addScore,
    incrementCombo,
    resetCombo,
    addMiss,
    setLastGrade,
    setTenouchi,
    recordGrade,
    recordTargetEvent,
  } = useGameStore.getState();

  function recordTargetResult({
    result,
    grade,
    points = 0,
    timingError = null,
    tenouchi = false,
    comboAfter = 0,
    timingSigned,
    snap,
    bladeSpeed,
    directionAlignment,
    centerAccuracy,
  }: {
    result: "hit" | "missed" | "wrong-direction";
    grade: Grade;
    points?: number;
    timingError?: number | null;
    tenouchi?: boolean;
    comboAfter?: number;
    // Calibration fields — only present on real impacts (see call site
    // below); a "missed" (never reached) result has none of these.
    timingSigned?: number;
    snap?: number;
    bladeSpeed?: number;
    directionAlignment?: number;
    centerAccuracy?: number;
  }) {
    recordTargetEvent({
      id: noteId,
      t: note.t,
      dir: note.dir,
      hand: note.hand,
      size: target.size,
      speed: target.speed,
      appearance: target.appearance,
      seq: note.seq,
      result,
      grade,
      points,
      timingError,
      tenouchi,
      comboAfter,
      timingSigned,
      snap,
      bladeSpeed,
      directionAlignment,
      centerAccuracy,
    });
  }

  useEffect(() => {
    rimMat.uniforms.uPower.value = target.rimPower;
  }, [rimMat, target.rimPower]);

  useEffect(() => {
    if (slice === "none") return;

    const tl = gsap.timeline({
      onComplete: () => onRemove(wasHitRef.current, pointsRef.current),
    });

    if (slice === "hit" && topRef.current && bottomRef.current) {
      tl.to(
        topRef.current.position,
        { y: "+=0.55", z: "+=0.14", duration: 0.45, ease: "power2.out" },
        0,
      );
      tl.to(
        bottomRef.current.position,
        { y: "-=0.55", z: "+=0.14", duration: 0.45, ease: "power2.out" },
        0,
      );
      tl.to(
        [topRef.current.scale, bottomRef.current.scale],
        { x: 0.01, y: 0.01, z: 0.01, duration: 0.18, ease: "power2.in" },
        0.28,
      );
      if (flashSlotRef.current) {
        gsap.fromTo(
          flashSlotRef.current,
          { intensity: 12 },
          { intensity: 0, duration: 0.4, ease: "power3.out" },
        );
      }
      // Brief amber flash on cut — hemispheres snap to amber then settle back
      // to the block's own direction color, on top of the burst light above.
      if (topMatRef.current && bottomMatRef.current) {
        const amberFlash = new Color(themeColors.amber);
        const settleTo = new Color(color);
        [topMatRef.current, bottomMatRef.current].forEach((m) => {
          m.emissive.copy(amberFlash);
          gsap.to(m.emissive, {
            r: settleTo.r,
            g: settleTo.g,
            b: settleTo.b,
            duration: 0.12,
            ease: "power2.out",
          });
        });
      }
      // Grade kanji: scale-punch from 2.2× → 1× then float up
      if (gradeGroupRef.current) {
        gsap.fromTo(
          gradeGroupRef.current.scale,
          { x: 2.2, y: 2.2, z: 2.2 },
          { x: 1.0, y: 1.0, z: 1.0, duration: 0.2, ease: "back.out(3)" },
        );
        gsap.fromTo(
          gradeGroupRef.current.position,
          { y: 0 },
          { y: 0.55, duration: 0.6, ease: "power3.out" },
        );
      }
    } else {
      if (groupRef.current) {
        tl.to(groupRef.current.scale, {
          x: 0.01,
          y: 0.01,
          z: 0.01,
          duration: 0.12,
        });
      }
    }
  }, [slice]);

  useFrame(() => {
    if (!groupRef.current || hitRef.current) return;
    // Freeze movement + collision while paused (or any non-playing phase) so the
    // clock can stop without blocks drifting, auto-missing, or registering hits.
    if (useGameStore.getState().phase !== "playing") return;

    const t = getCurrentTime();
    const elapsed = t - scheduledSpawnTime;
    const z = SPAWN_Z + elapsed * target.travelSpeed;
    groupRef.current.position.z = z;

    // Approach telegraph — ramps up as the block nears the strike window
    // (hit plane at z=0), timed so the visual peak lands near contact.
    const strikeDistance = Math.abs(z - target.strikeZ);
    const proximity = Math.min(Math.max(1 - strikeDistance / 2.4, 0), 1);
    rimMat.uniforms.uIntensity.value = 0.45 + proximity * 2.0;
    rimMat.uniforms.uTelegraph.value = proximity * TELEGRAPH_MIX_MAX;
    bodyMat.uniforms.uPulse.value = proximity;
    auraMat.uniforms.uPulse.value = proximity;
    coreMat.uniforms.uPulse.value = proximity;
    highlightMat.uniforms.uPulse.value = proximity;
    trailMat.uniforms.uPulse.value = proximity;

    // Approach pulse peaks at the strike plane instead of drifting with a clock
    // sine, so the telegraph is temporally coupled to contact.
    const pulse = 1 + proximity * target.telegraphScalePulse;
    groupRef.current.scale.setScalar(pulse);

    if (z > MISS_Z) {
      hitRef.current = true;
      wasHitRef.current = false;
      gradeKanji.current = GRADE_KANJI["MISS"];
      gradeEnglish.current = GRADE_ENGLISH["MISS"];
      comboLabel.current = "";
      gradeColor.current = GRADE_COLORS["MISS"];
      addMiss();
      resetCombo();
      setLastGrade("MISS");
      recordGrade("MISS");
      recordTargetResult({ result: "missed", grade: "MISS" });
      setTimeout(() => setLastGrade(null), 600);
      setSlice("miss");
      return;
    }

    if (z > SPAWN_Z * 0.4) {
      groupRef.current.getWorldPosition(tmpPos.current);
      const { settings } = useGameStore.getState();
      const hand = requiredHandForWield(note.hand, settings.wield);
      const result = checkCollision(tmpPos.current, target.radius, hand);

      if (result) {
        playSlashSound();

        const expectedDir = DIR_VECTORS[note.dir];
        const directionAlignment = expectedDir
          ? result.cutDir.dot(expectedDir)
          : 1;
        const centerAccuracy = Math.max(0, 1 - result.distance / target.radius);
        let dirOk = true;
        if (expectedDir) {
          dirOk = directionAlignment > DIRECTION_DOT_THRESHOLD;
        }

        hitRef.current = true;
        const err = Math.abs(t - note.t);

        if (!dirOk) {
          wasHitRef.current = false;
          gradeKanji.current = GRADE_KANJI["MISS"];
          gradeEnglish.current = GRADE_ENGLISH["MISS"];
          comboLabel.current = "";
          gradeColor.current = GRADE_COLORS["MISS"];
          result.pulse?.("MISS", false);
          addMiss();
          resetCombo();
          setLastGrade("MISS");
          recordGrade("MISS");
          recordTargetResult({
            result: "wrong-direction",
            grade: "MISS",
            timingError: err,
            timingSigned: t - note.t,
            snap: result.snap,
            bladeSpeed: result.speed,
            directionAlignment,
            centerAccuracy,
          });
          setTimeout(() => setLastGrade(null), 600);
          setSlice("miss");
          return;
        }

        const combo = useGameStore.getState().combo;
        const grade: Grade = getCutGrade({
          timingError: err,
          snap: result.snap,
          directionAlignment,
          centerAccuracy,
        });
        const isTenouchi = result.snap >= TENOUCHI_SNAP_THRESHOLD;
        const directionBonus = expectedDir ? DIRECTION_BONUS : 0;
        const points = Math.round(
          (BASE_POINTS[grade] + directionBonus) *
            comboMultiplier(combo) *
            tenouchiBonus(result.snap),
        );
        pointsRef.current = points;
        addScore(points);
        incrementCombo();
        setLastGrade(grade);
        recordGrade(grade);
        recordTargetResult({
          result: "hit",
          grade,
          points,
          timingError: err,
          tenouchi: isTenouchi,
          comboAfter: combo + 1,
          timingSigned: t - note.t,
          snap: result.snap,
          bladeSpeed: result.speed,
          directionAlignment,
          centerAccuracy,
        });
        setTenouchi(isTenouchi);
        result.pulse?.(grade, isTenouchi);
        gradeKanji.current = GRADE_KANJI[grade] ?? grade;
        gradeEnglish.current = GRADE_ENGLISH[grade] ?? grade;
        comboLabel.current = combo + 1 > 1 ? `${combo + 1}x COMBO` : "";
        gradeColor.current = GRADE_COLORS[grade];
        tenouchiBadge.current = isTenouchi;
        flashSlotRef.current = triggerHitFlash(
          tmpPos.current,
          gradeColor.current,
        );
        setTimeout(() => {
          setLastGrade(null);
          setTenouchi(false);
        }, 700);
        wasHitRef.current = true;
        setSlice("hit");
      }
    }
  });

  const half = target.bodySize / 2;
  const cutArrowInverted = useGameStore.getState().settings.cutArrowInverted;
  const arrowRot = (cutArrowInverted ? DIR_ARROW_ROT_AGAINST : DIR_ARROW_ROT)[
    note.dir
  ];

  return (
    <group ref={groupRef} position={[note.laneX, worldY, SPAWN_Z]}>
      {slice === "none" && (
        <>
          {/* Block body - archetype form */}
          <mesh
            scale={
              target.form === "teardrop" ? [1.78, 0.78, 0.34] : target.glowScale
            }
            material={auraMat}
            renderOrder={1}
          >
            <sphereGeometry args={[half, 24, 12]} />
          </mesh>

          {target.form === "teardrop" ? (
            <>
              <mesh
                position={[-half * 0.92, 0, -half * 0.28]}
                scale={[1.85, 0.46, 0.2]}
                material={trailMat}
                renderOrder={1}
              >
                <sphereGeometry args={[half, 16, 8]} />
              </mesh>
              <mesh
                position={[-half * 0.28, 0, 0]}
                scale={[1.08, 0.82, 0.72]}
                material={bodyMat}
                renderOrder={2}
              >
                <sphereGeometry args={[half * 0.82, 24, 16]} />
              </mesh>
              <mesh
                position={[half * 0.22, 0, 0]}
                rotation={[0, 0, -Math.PI / 2]}
                scale={[1, 0.94, 0.72]}
                material={bodyMat}
                renderOrder={2}
              >
                <coneGeometry args={[half * 0.68, half * 1.42, 32]} />
              </mesh>
            </>
          ) : (
            <mesh material={bodyMat} renderOrder={2}>
              <sphereGeometry args={[half, 24, 16]} />
            </mesh>
          )}

          <mesh
            position={
              target.form === "teardrop"
                ? [-half * 0.08, 0, half * 0.08]
                : [0, 0, half * 0.04]
            }
            scale={
              target.form === "teardrop"
                ? [0.72, 0.42, 0.3]
                : [0.48, 0.48, 0.36]
            }
            material={coreMat}
            renderOrder={3}
          >
            <sphereGeometry args={[half, 24, 12]} />
          </mesh>

          <mesh
            position={
              target.form === "teardrop"
                ? [-half * 0.34, half * 0.32, half * 0.34]
                : [-half * 0.24, half * 0.3, half * 0.46]
            }
            rotation={[0.12, 0, -0.38]}
            scale={
              target.form === "teardrop"
                ? [0.42, 0.22, 0.055]
                : [0.34, 0.22, 0.06]
            }
            material={highlightMat}
            renderOrder={4}
          >
            <sphereGeometry args={[half, 16, 8]} />
          </mesh>

          {/* Outer glow shell — fresnel rim. The silhouette-edge glow is what
              reads as volume/legibility against the dark dōjō background;
              intensity ramps via the approach telegraph in useFrame above. */}
          <mesh
            scale={target.form === "teardrop" ? [1.32, 1.02, 0.88] : 1.22}
            material={rimMat}
            renderOrder={5}
          >
            <sphereGeometry args={[half, 24, 12]} />
          </mesh>

          {/* No per-target pointLight here (removed) — targets spawn/despawn
              continuously during a phase, and mounting/unmounting a light
              changes NUM_POINT_LIGHTS, forcing three.js to synchronously
              recompile every lit material in the scene (the whole dōjō +
              these direction/hemisphere meshes). That recompile was the main
              thread stall behind the "freeze then jump forward" symptom —
              position is computed from audio.currentTime each frame
              (see useFrame above), so a stalled frame shows up as a visible
              skip once render catches up. The 6 additive/emissive
              ShaderMaterials above already read fine without local lighting. */}

          {/* Directional indicator - front-mounted bevel/notch */}
          {note.dir !== "any" ? (
            <group position={[0, 0, half + 0.075]} rotation={arrowRot}>
              <mesh position={[0.015, -0.012, -0.012]} scale={[0.94, 0.94, 1]}>
                <circleGeometry args={[half * 0.92, 20]} />
                <meshBasicMaterial
                  color={shadowColor}
                  transparent
                  opacity={0.24}
                  depthWrite={false}
                />
              </mesh>
              <DirectionChevron
                size={target.bodySize}
                accent={target.accent}
                shadow={shadowColor}
              />
            </group>
          ) : (
            <group position={[0, 0, half + 0.075]}>
              <mesh position={[0.015, -0.012, -0.012]} scale={[0.94, 0.94, 1]}>
                <circleGeometry args={[half * 0.92, 20]} />
                <meshBasicMaterial
                  color={shadowColor}
                  transparent
                  opacity={0.24}
                  depthWrite={false}
                />
              </mesh>
              <mesh position={[0.008, -0.004, 0.008]}>
                <boxGeometry
                  args={[target.bodySize * 0.68, target.bodySize * 0.16, 0.018]}
                />
                <meshStandardMaterial
                  color={shadowColor}
                  emissive={shadowColor}
                  emissiveIntensity={0.9}
                />
              </mesh>
              <mesh position={[0, 0, 0.02]}>
                <boxGeometry
                  args={[target.bodySize * 0.68, target.bodySize * 0.14, 0.018]}
                />
                <meshStandardMaterial
                  color={target.accent}
                  emissive={target.accent}
                  emissiveIntensity={2.8}
                />
              </mesh>
              <mesh position={[0.008, -0.004, 0.008]}>
                <boxGeometry
                  args={[target.bodySize * 0.16, target.bodySize * 0.68, 0.018]}
                />
                <meshStandardMaterial
                  color={shadowColor}
                  emissive={shadowColor}
                  emissiveIntensity={0.9}
                />
              </mesh>
              <mesh position={[0, 0, 0.02]}>
                <boxGeometry
                  args={[target.bodySize * 0.14, target.bodySize * 0.68, 0.018]}
                />
                <meshStandardMaterial
                  color={target.accent}
                  emissive={target.accent}
                  emissiveIntensity={2.8}
                />
              </mesh>
            </group>
          )}
        </>
      )}

      {slice === "hit" && (
        <>
          {/* Top hemisphere */}
          <mesh ref={topRef} position={[0, 0, 0]}>
            <sphereGeometry
              args={[half, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]}
            />
            <meshStandardMaterial
              ref={topMatRef}
              color={color}
              emissive={color}
              emissiveIntensity={1.8}
              side={2}
            />
          </mesh>
          {/* Bottom hemisphere */}
          <mesh ref={bottomRef} position={[0, 0, 0]}>
            <sphereGeometry
              args={[half, 16, 8, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2]}
            />
            <meshStandardMaterial
              ref={bottomMatRef}
              color={color}
              emissive={color}
              emissiveIntensity={1.8}
              side={2}
            />
          </mesh>

          {/* Burst light lives in the always-mounted HitFlashPool (see
              hitFlash.ts) — a pointLight mounted/unmounted per hit here
              would thrash NUM_POINT_LIGHTS and force shader recompiles. */}

          {/* Particle burst */}
          <CutParticles color={color} />

          {/* Grade feedback — kanji stays decorative; English carries meaning. */}
          <group ref={gradeGroupRef}>
            <Billboard>
              <Text
                position={[0, 0.08, 0]}
                fontSize={0.16}
                color={gradeColor.current}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.01}
                outlineColor="#000000"
              >
                {gradeKanji.current}
              </Text>
              <Text
                position={[0, -0.08, 0]}
                fontSize={0.07}
                color={gradeColor.current}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.006}
                outlineColor="#000000"
                letterSpacing={0.04}
              >
                {gradeEnglish.current}
              </Text>
              {comboLabel.current && (
                <Text
                  position={[0, -0.17, 0]}
                  fontSize={0.052}
                  color={gradeColor.current}
                  anchorX="center"
                  anchorY="middle"
                  outlineWidth={0.005}
                  outlineColor="#000000"
                  letterSpacing={0.04}
                >
                  {comboLabel.current}
                </Text>
              )}
              {tenouchiBadge.current && (
                <Text
                  position={[0, comboLabel.current ? -0.24 : -0.17, 0]}
                  fontSize={0.048}
                  color={themeColors.violet}
                  anchorX="center"
                  anchorY="middle"
                  outlineWidth={0.005}
                  outlineColor="#000000"
                  letterSpacing={0.04}
                >
                  tenouchi
                </Text>
              )}
            </Billboard>
          </group>
        </>
      )}
    </group>
  );
}
